import { Box, Typography } from '@mui/material';
import { SECTION_COPY } from '../../content';
import { sectionSpacingSx } from '../../theme';
import { ContentSection } from '../ContentSection';
import type { ProseSectionsProps } from './types';

/**
 * Description, Why, What and How — the four prose sections a phase and a
 * process both carry, in that order.
 *
 * **One component for both levels, because they are the same four fields.** The
 * designs head them differently on each page — "What" over `descriptionText` on
 * a phase, "Description" over the same field on a process — which would make
 * "What" mean two different things one click apart. The heading follows the
 * field, and the field's name is Confluence's own: the scheduler matches a
 * heading called "Description", so that is what it is called here.
 *
 * **A section with no text is absent, not empty.** These fields are optional on
 * the record for a measured reason — twelve process pages have no "How" — and a
 * heading over nothing reads as content that failed to load.
 */
export const ProseSections = ({ content }: ProseSectionsProps) => {
  const sections: { heading: string; caption: string; text: string }[] = [
    { ...SECTION_COPY.description, text: content.descriptionText },
    { ...SECTION_COPY.why, text: content.whyText },
    { ...SECTION_COPY.what, text: content.whatText },
    { ...SECTION_COPY.how, text: content.howText },
  ].flatMap((section) =>
    section.text === undefined || section.text === ''
      ? []
      : [{ heading: section.heading, caption: section.caption, text: section.text }],
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <Box>
      {sections.map((section, index) => (
        <Box key={section.heading} sx={index === 0 ? undefined : sectionSpacingSx}>
          <ContentSection heading={section.heading} caption={section.caption}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {section.text}
            </Typography>
          </ContentSection>
        </Box>
      ))}
    </Box>
  );
};
